import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import api from '../lib/api';
import ShareButton from '../components/ShareButton';
import { useAuth } from '../context/AuthContext';

const MAX_POLLS = 12;

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const { checkAuth } = useAuth();
  const sessionId = params.get('session_id');
  const kind = params.get('kind'); // 'booking' | 'free' | null (membership)
  const [state, setState] = useState('polling'); // polling | success | pending | error
  const [medSlug, setMedSlug] = useState(null);
  const polls = useRef(0);

  useEffect(() => {
    // Free booking: no payment to poll.
    if (kind === 'free') { setState('success'); return; }
    if (!sessionId) { setState('error'); return; }
    const statusUrl = kind === 'booking' ? `/bookings/status/${sessionId}`
      : kind === 'donation' ? `/donations/status/${sessionId}`
      : kind === 'meditation' ? `/meditations/purchase/status/${sessionId}`
      : kind === 'meditation_bundle' ? `/meditations/bundle-status/${sessionId}`
      : kind === 'consultation' ? `/consultations/status/${sessionId}`
      : `/payments/status/${sessionId}`;
    let timer;
    const poll = async () => {
      try {
        const res = await api.get(statusUrl);
        if (res.data.payment_status === 'paid' || (kind === 'consultation' && ['authorized', 'captured'].includes(res.data.payment_status))) {
          setState('success');
          if (res.data.slug) setMedSlug(res.data.slug);
          if (kind !== 'booking') checkAuth();
          return;
        }
        if (['failed', 'expired'].includes(res.data.payment_status)) {
          setState('error');
          return;
        }
      } catch (_e) { /* keep polling */ }
      polls.current += 1;
      if (polls.current >= MAX_POLLS) { setState('pending'); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, kind, checkAuth]);

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-6">
      <div className="max-w-md w-full p-10 rounded-3xl bg-white/80 border border-rose/50 text-center">
        {state === 'polling' && (
          <>
            <Loader2 className="w-12 h-12 animate-spin text-mahogany mx-auto" />
            <h1 className="mt-5 font-serif-display text-3xl text-burgundy">Confirming your payment…</h1>
            <p className="mt-2 text-mahogany/80">Please wait a moment while we activate your membership.</p>
          </>
        )}
        {state === 'success' && (
          <>
            <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto" />
            <h1 className="mt-5 font-serif-display text-3xl text-burgundy">{kind === 'donation' ? 'Thank you! 🙏' : (kind === 'meditation' || kind === 'meditation_bundle') ? 'Unlocked 🧘' : (kind === 'booking' || kind === 'free' ? 'You\u2019re booked! 🌸' : 'Welcome aboard! 🌸')}</h1>
            <p className="mt-2 text-mahogany/80">{kind === 'donation' ? 'Your donation has been received with deep gratitude. It directly supports our community and mission.' : kind === 'meditation' ? 'Your meditation technique is now unlocked for good. Practise it anytime from your library.' : kind === 'meditation_bundle' ? 'The whole collection is now unlocked for good. Explore every technique in the group.' : (kind === 'booking' || kind === 'free' ? 'Your spot is confirmed. We can\u2019t wait to celebrate with you.' : 'Your membership is now active. Enjoy your benefits.')}</p>
            <div className="mt-6 flex flex-col items-center gap-3">
              {(kind === 'meditation' || kind === 'meditation_bundle') && (
                <Link to={medSlug ? `/meditations/${medSlug}` : '/meditations'} className="pill-btn inline-flex" data-testid="go-to-technique">{kind === 'meditation_bundle' ? 'Browse the collection' : 'Open the technique'}</Link>
              )}
              {kind === 'donation' && sessionId && (
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/donations/${sessionId}/receipt`} target="_blank" rel="noopener noreferrer" className="pill-btn inline-flex" data-testid="download-receipt">
                  Download Tax Receipt
                </a>
              )}
              <Link to="/dashboard" className="pill-btn-outline inline-flex">Go to Dashboard</Link>
              <div className="flex items-center gap-2 text-sm text-mahogany/70">
                <span>Know someone who&rsquo;d love this?</span>
                <ShareButton label="Invite a friend" url={`${window.location.origin}/events`} title="Osho Universal" text="I just booked with Osho Universal — join me! 🌸" testid="invite-friend-share" />
              </div>
            </div>
          </>
        )}
        {state === 'pending' && (
          <>
            <Loader2 className="w-12 h-12 text-mahogany mx-auto" />
            <h1 className="mt-5 font-serif-display text-3xl text-burgundy">Payment processing</h1>
            <p className="mt-2 text-mahogany/80">This is taking a little longer than usual. Your membership will activate shortly — check your dashboard in a few minutes.</p>
            <Link to="/dashboard" className="pill-btn mt-6 inline-flex">Go to Dashboard</Link>
          </>
        )}
        {state === 'error' && (
          <>
            <XCircle className="w-14 h-14 text-mahogany mx-auto" />
            <h1 className="mt-5 font-serif-display text-3xl text-burgundy">Something went wrong</h1>
            <p className="mt-2 text-mahogany/80">We couldn&apos;t confirm this payment. If you were charged, it will be reflected soon.</p>
            <Link to="/membership" className="pill-btn mt-6 inline-flex">Back to Membership</Link>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
